import { yupResolver } from "@hookform/resolvers/yup";
import {
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import * as yup from "yup";
import BookingInfoCard from "@/components/booking/bookingInfoCard";
import PaymentCardFormSection from "@/components/booking/payment/paymentCardFormSection";
import { CollectCreditCardFormInput } from "@/components/booking/steps/collectCreditCard";
import { useClientBookingContext } from "@/contexts/clientBookingContext";
import { useVerifyClient2FaCodeMutation } from "@/graphql/mutations/verifyClient2FACode.graphql.types";
import useBookingClientDetails from "@/hooks/booking/useBookingClientDetails";
import useCreditCardInputs from "@/hooks/payments/useCreditCardInputs";
import { BookingStep } from "@/types/bookingTypes";
import { BookingReturningClientErrors } from "@/utils/booking";
import { hasDjangoMutationError } from "@/utils/djangoMutationError";
import useErrorLogger from "@/utils/useErrorLogger";

const schema = yup.object().shape({
  zipCode: yup
    .string()
    .length(5, "Please provide valid zip code")
    .required("Enter your zip code."),
});

export default function ReturningClientAddNewCard() {
  const logError = useErrorLogger();
  const { code, medspaData, setCard, clientDetails, setStep, setCodeExpired } =
    useClientBookingContext();

  const { getClientSecret } = useBookingClientDetails();

  const {
    paymentCardValidationInfo,
    isPaymentCardInfoValid,
    showPaymentCardEmptyFieldErrors,
    setShowPaymentCardEmptyFieldErrors,
    onElementChange,
  } = useCreditCardInputs();

  const methods = useForm<CollectCreditCardFormInput>({
    resolver: yupResolver(schema),
    defaultValues: {
      zipCode: "",
    },
  });

  const stripe = useStripe();
  const elements = useElements();
  const [verifyClient2FACode] = useVerifyClient2FaCodeMutation();

  const setupCard = async () => {
    if (!stripe || !elements) return;
    if (!isPaymentCardInfoValid) {
      setShowPaymentCardEmptyFieldErrors(true);
      return;
    }
    try {
      const clientSecret = await getClientSecret(clientDetails);

      const { error } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: elements.getElement(CardNumberElement),
          billing_details: {
            address: {
              postal_code: methods.getValues().zipCode,
            },
          },
        },
      });

      if (error) {
        logError(error);
        toast.error(error.message);
        return;
      }

      const { data } = await verifyClient2FACode({
        variables: {
          clientPhoneNumber: clientDetails.phone,
          code,
          medspaSlug: medspaData.slug,
        },
      });

      setCard(data.verifyClient2faCode.clientData.card);
      setStep(BookingStep.RETURNING_CLIENT);
    } catch (error) {
      if (error.message === BookingReturningClientErrors.CODE_EXPIRED) {
        setStep(BookingStep.CODE_INPUT);
        setCodeExpired(true);
        return;
      }
      logError(error);
      toast.error(
        hasDjangoMutationError(error)
          ? error.message
          : "Unable to add new card. Please contact support team."
      );
    }
  };

  return (
    <BookingInfoCard>
      <FormProvider {...methods}>
        <PaymentCardFormSection
          setupCard={setupCard}
          paymentCardValidationInfo={paymentCardValidationInfo}
          showPaymentCardEmptyFieldErrors={showPaymentCardEmptyFieldErrors}
          onElementChange={onElementChange}
          showConsentToCharge={false}
          showAutomatedMessage={false}
          title="Enter your card's details below"
          buttonTitle="Save changes"
        />
      </FormProvider>
    </BookingInfoCard>
  );
}
